import ToolbarPopover from "../ToolbarPopover/ToolbarPopover";
import { Select, Counter, Text, Flex, ToolIcon } from "@theorgchartdev/storybook";
import { useRef, useState } from "react";
import * as StateLib from "@/Utils/StateLib";
import { ConvertUnitsToPixels, ConvertPixelsToUnits } from "@/Utils/DataLib";
import { UpdateBoxProperty } from "@/Utils/StateLib";
import { getHoverTipContent } from "@/Utils/Misc";
import {
  kSizingModeOptions,
  enumBoxWidthMode,
  enumUpdateProperty,
  kMaxBoxSize
} from "@coreapp/constants";
import { StringCodeToHSizingMode } from "@coreapp/utils/StringCodeToHSizingMode";
import { HSizingModeToStringCode } from "@coreapp/utils/HSizingModeToStringCode";
import { appstr } from "@coreapp/utils/appstr";

const BuildBoxWidthOptions = updateBoxWidthMode => {
  let widthOptions = [];
  const wsMgr = StateLib.GetWSMgr();
  let optionsList = [kSizingModeOptions[0], kSizingModeOptions[1]];
  if (wsMgr.IsStdBox()) optionsList.push(kSizingModeOptions[2]);
  optionsList.push(kSizingModeOptions[3]);
  for (let i of optionsList) {
    let option = {};
    option.label = appstr(i);
    option.value = StringCodeToHSizingMode(i);
    option.clickHandler = updateBoxWidthMode;
    widthOptions.push(option);
  }
  return widthOptions;
};

const BoxWidthPopover = ({ layout }) => {
  const openCtrlRef = useRef(null);
  const useMetric = StateLib.UseMetric();

  const currentWidthMode = layout.GetWidthSizingMode() ?? enumBoxWidthMode.fixedwidth;
  const [widthMode, setWidthMode] = useState(currentWidthMode);

  const UpdateBoxWidthMode = target => {
    const wsMgr = StateLib.GetWSMgr();
    const newWidthMode = target.value;
    layout.SetWidthSizingMode(newWidthMode);
    setWidthMode(newWidthMode);
    wsMgr.UpdateBoxSizingMode(true);
    StateLib.ReRenderCanvas();
  };

  const UpdateFixedWidth = newFixedWidth => {
    var pixels = ConvertUnitsToPixels(newFixedWidth);
    if (pixels < 1) pixels = 0;
    if (pixels > kMaxBoxSize) pixels = kMaxBoxSize;
    const current = layout.GetBoxWidth();
    const delta = pixels - current;
    UpdateBoxProperty(enumUpdateProperty.boxWidth, delta);
  };

  const UpdateMinWidth = newFixedWidth => {
    var pixels = ConvertUnitsToPixels(newFixedWidth);
    if (pixels < 1) pixels = 0;
    if (pixels > kMaxBoxSize) pixels = kMaxBoxSize;
    layout.SetWidthMin(pixels);
    UpdateBoxProperty(enumUpdateProperty.boxMinWidth, pixels);
  };

  const UpdateMaxWidth = newFixedWidth => {
    var pixels = ConvertUnitsToPixels(newFixedWidth);
    if (pixels < 1) pixels = 0;
    if (pixels > kMaxBoxSize) pixels = kMaxBoxSize;
    layout.SetWidthMax(pixels);
    UpdateBoxProperty(enumUpdateProperty.boxMaxWidth, pixels);
  };

  let currentBoxWidth = layout?.GetBoxWidth();
  if (currentBoxWidth != null) currentBoxWidth = ConvertPixelsToUnits(currentBoxWidth, useMetric);

  let currentMinWidth = layout?.GetWidthMin();
  if (currentMinWidth != null) currentMinWidth = ConvertPixelsToUnits(currentMinWidth, useMetric);

  let currentMaxWidth = layout?.GetWidthMax();
  if (currentMaxWidth != null) currentMaxWidth = ConvertPixelsToUnits(currentMaxWidth, useMetric);

  const showFixedWidth = widthMode === enumBoxWidthMode.fixedwidth;

  const inputStyles = { width: "60px" };
  const unitLabel = useMetric ? "cm" : "in";

  const widthTrigger = (
    <ToolIcon
      dataTestId="box-layout-editor-toolbar-width-btn"
      name={"arrows-left-right-to-line"}
      tooltipProps={{ ...getHoverTipContent("Box width"), referenceOpenCtrlRef: openCtrlRef }}
    />
  );
  return (
    <ToolbarPopover
      openCtrlRef={openCtrlRef}
      key={1}
      icon={"arrows-left-right-to-line"}
      otherPopoverProps={{ offsetOptions: -2, reference: widthTrigger }}
    >
      <Flex direction={"column"} gap={"12px"}>
        <Text modifier={"medium"} size={"small"}>
          {appstr("boxwidth")}
        </Text>
        <Flex direction={"column"} gap={"12px"} alignItems={"flex-end"}>
          <Select
            dataTestId="box-layout-editor-toolbar-width-mode-select"
            options={BuildBoxWidthOptions(UpdateBoxWidthMode)}
            label={appstr(HSizingModeToStringCode(widthMode))}
            value={widthMode}
            selectSize={"medium"}
          />
          {showFixedWidth ? (
            <Flex alignItems={"center"} justifyContent={"space-between"} gap={"8px"}>
              <Counter
                key={2}
                onChange={UpdateFixedWidth}
                currentValue={parseFloat(currentBoxWidth)}
                defaultValue={parseFloat(currentBoxWidth)}
                inputStyles={inputStyles}
                inputProps={{ "data-testid": "box-layout-editor-toolbar-width-input" }}
                allowDecimals={true}
                decimalPlaces={3}
                incrementValue={0.1}
                size={"small"}
              />
              <Text size={"small"} colorModifier={"grey"}>
                {unitLabel}
              </Text>
            </Flex>
          ) : (
            <Flex direction={"column"} gap={"4px"}>
              <Flex alignItems={"center"} justifyContent={"space-between"} gap={"8px"}>
                <div
                  style={{
                    width: "30px",
                    marginRight: "auto",
                    marginLeft: "0"
                  }}
                >
                  <Text size={"small"} colorModifier={"grey"}>
                    {appstr("Min")}
                  </Text>
                </div>
                <Counter
                  key={3}
                  onChange={UpdateMinWidth}
                  currentValue={parseFloat(currentMinWidth)}
                  defaultValue={parseFloat(currentMinWidth)}
                  allowDecimals={true}
                  inputStyles={inputStyles}
                  inputProps={{ disabled: true }}
                  decimalPlaces={3}
                  incrementValue={0.1}
                  size={"small"}
                />
                <Text size={"small"} colorModifier={"grey"}>
                  {unitLabel}
                </Text>
              </Flex>
              <Flex alignItems={"center"} justifyContent={"space-between"} gap={"8px"}>
                <div
                  style={{
                    width: "30px",
                    marginRight: "auto",
                    marginLeft: "0"
                  }}
                >
                  <Text size={"small"} colorModifier={"grey"}>
                    {appstr("Max")}
                  </Text>
                </div>
                <Counter
                  key={4}
                  onChange={UpdateMaxWidth}
                  currentValue={parseFloat(currentMaxWidth)}
                  defaultValue={parseFloat(currentMaxWidth)}
                  inputStyles={inputStyles}
                  allowDecimals={true}
                  inputProps={{ disabled: true }}
                  decimalPlaces={3}
                  incrementValue={0.1}
                  size={"small"}
                />
                <Text size={"small"} colorModifier={"grey"}>
                  {unitLabel}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </ToolbarPopover>
  );
};

export default BoxWidthPopover;
